import { Injectable } from '@angular/core';
import { SanityClient, createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import {
  AllGraphDescriptions,
  AnalysisDescription,
  Article,
  ContractDesc,
  ImageWithMeta,
  SanityImageSizesObject,
  SanityImageSizesOpt
} from './sanity.types';
import {
  FitMode,
  ImageFormat,
  SanityImageHotspot
} from '@sanity/image-url/lib/types/types';
import { PortableTextBlock } from '@portabletext/types';
import { Platform } from '@ionic/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { contentBlockComponents } from './sanity.utils';
import { toHTML } from '@portabletext/to-html';
import { theTime } from 'src/app/utils/date-utils';

@Injectable({
  providedIn: 'root'
})
export class SanityService {
  client: SanityClient;
  builder: ImageUrlBuilder;
  platformQuery: string;

  constructor(private platform: Platform, private sanitizer: DomSanitizer) {
    this.client = createClient({
      projectId: 'n5xeg4df',
      dataset: 'ishavskraft-001',
      apiVersion: theTime().toPlainDateString(),
      useCdn: false
    });
    this.builder = imageUrlBuilder(this.client);
    this.platformQuery = this.platform.is('capacitor') ? 'app' : 'web';
  }

  contentToSafeHtml(content: PortableTextBlock[]): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      toHTML(content, {
        components: contentBlockComponents(
          this.sanitizer,
          this.platform.width() > 700
        )
      })
    );
  }

  async getNyttigArticles() {
    const articles = await this.client.fetch(
      `*[_type == "article" && publish_in_app == true && app_publish_location in ["all", "${this.platformQuery}"] && (!defined(language) || language match "nb")] | order(publishedAt desc) | order(sort_order asc) { 
        ...,
        image{
          ...,
          asset->{
            ...,
            metadata{dimensions}
          }
        }
      }`
    );
    return this.processArticles(articles);
  }
  async getNyhetNyttigArticles() {
    const articles = await this.client.fetch(
      `*[_type == "article" && "Nyheter" in categories[].name && publish_in_app == true && app_publish_location in ["all", "${this.platformQuery}"] && (!defined(language) || language match "nb")] | order(publishedAt desc) | order(sort_order asc) { 
        ...,
        image{
          ...,
          asset->{
            ...,
            metadata{dimensions}
          }
        }
      }`
    );
    return this.processArticles(articles);
  }
  async getTilbudNyttigArticles() {
    const articles = await this.client.fetch(
      `*[_type == "article" && "Tilbud" in categories[].name && publish_in_app == true && app_publish_location in ["all", "${this.platformQuery}"] && (!defined(language) || language match "nb")] | order(publishedAt desc) | order(sort_order asc) { 
        ...,
        image{
          ...,
          asset->{
            ...,
            metadata{dimensions}
          }
        }
      }`
    );
    return this.processArticles(articles);
  }
  async getForsideArticles() {
    const articles = await this.client.fetch(
      `*[_type == "article" && "Forside app" in categories[].name && publish_in_app == true && app_publish_location in ["all", "${this.platformQuery}"] && (!defined(language) || language match "nb")] | order(publishedAt desc) | order(sort_order asc) { 
        ...,
        image{
          ...,
          asset->{
            ...,
            metadata{dimensions}
          }
        }
      }`
    );
    return this.processArticles(articles);
  }
  async getArticleBySlug(slug: string) {
    const articles = await this.client.fetch(
      `*[_type == "article" && publish_in_app == true && app_publish_location in ["all", "${this.platformQuery}"] && slug.current == "${slug}"] | order(publishedAt desc) {
        ...,
        image{
          ...,
          asset->{
            ...,
            metadata{dimensions}
          }
        },
        content[]{
          ...,
          _type == "block" => {
            ...,
            children[]{
              ...,
              _type == "fileReference" => {
                ...,
                "url": file.asset->url,
              }
            }
          }
        }
      }`
    );

    if (articles.length) {
      const article = articles.find((a: Article) => a.language && a.language === 'nb') || articles[0];

      return {
        ...article,
        image: article.image?.asset?._id
          ? {
            ...article.image,
            ...this.getSanityImageSizesObject(article.image.asset._id, {
              sizes: [
                [700, 394],
                [1402, 620]
              ],
              fit: 'min'
            })
          }
          : undefined,
        content: [
          ...article.content.map((x: PortableTextBlock & ImageWithMeta) => {
            if (x._type === 'image') {
              return {
                ...x,
                ...this.getSanityImageSizesObject(x.asset._ref, {
                  sizes: [
                    [700, 394],
                    [1402, 620]
                  ],
                  fit: 'min'
                })
              };
            }
            return { ...x };
          })
        ]
      };
    }
  }

  async getGraphDescriptions(): Promise<AllGraphDescriptions> {
    const document = await this.client.fetch(
      `*[_type == "app_graph_descs"] { ... }`
    );
    if (document[0]) {
      return document[0];
    }
  }
  async getAvtaleDescriptions(): Promise<ContractDesc[]> {
    const documents = await this.client.fetch(
      `*[_type == "app_contract_desc"] { ... }`
    );
    if (documents.length > 0) {
      return documents.map((desc: ContractDesc) => {
        return {
          ...desc,
          image: desc.image?.asset?._id
            ? {
              ...desc.image,
              ...this.getSanityImageSizesObject(desc.image.asset._id, {
                sizes: [
                  [700, 394],
                  [1402, 620]
                ],
                fit: 'min'
              })
            }
            : undefined
        };
      });
    }
  }

  async getAnalysisDescription(): Promise<AnalysisDescription> {
    const document = await this.client.fetch(
      `*[_type == "app_analysis_desc"] { 
        ..., 
        content[]{
          ...,
          _type == "block" => {
            ...,
            children[]{
              ...,
              _type == "fileReference" => {
                ...,
                "url": file.asset->url,
              }
            }
          }
      } }`
    );
    if (document[0]) {
      return document[0];
    }
  }

  urlFor(source: string) {
    return this.builder.image(source);
  }
  webpUrlFor = (source: string) =>
    this.builder.image(source).format(`webp`).url();
  jpgUrlFor = (source: string) =>
    this.builder.image(source).format(`jpg`).url();

  processArticles = (articles: Article[] | undefined): Article[] => {
    if (articles?.length > 0) {
      return articles.map((article: Article) => {
        return {
          ...article,
          image: article.image?.asset?._id
            ? {
              ...article.image,
              ...this.getSanityImageSizesObject(article.image.asset._id, {
                sizes: [
                  [700, 394],
                  [1402, 620]
                ],
                fit: 'min'
              })
            }
            : undefined
        };
      });
    }
    return [];
  };

  getSanityImageSrc = (
    id: string,
    opt: {
      width?: number;
      height?: number;
      fit?: FitMode;
      format?: ImageFormat;
      hotspot?: SanityImageHotspot;
    }
  ): string => {
    const format: ImageFormat = opt?.format ? opt.format : 'jpg';
    const defaultFit = 'min';

    // width, height, fit, hotspot
    if (
      opt?.width &&
      opt?.height &&
      opt?.fit &&
      opt?.hotspot?.x &&
      opt?.hotspot?.y
    )
      return `${this.urlFor(id)
        .format(format)
        .width(opt.width)
        .height(opt.height)
        .focalPoint(opt.hotspot.x, opt.hotspot.y)
        .crop('focalpoint')
        .fit(opt.fit)
        .url()}`;
    // width, height, fit
    else if (opt?.width && opt?.height && opt?.fit)
      return `${this.urlFor(id)
        .format(format)
        .width(opt.width)
        .height(opt.height)
        .fit(opt.fit)
        .url()}`;
    // width, height, (default fit)
    else if (opt?.width && opt?.height && !opt?.fit)
      return this.urlFor(id)
        .format(format)
        .width(opt.width)
        .height(opt.height)
        .fit(defaultFit)
        .url();
    // width, fit
    else if (opt?.width && !opt?.height && opt?.fit)
      return this.urlFor(id).format(format).width(opt.width).fit(opt.fit).url();
    // width, (default fit)
    else if (opt?.width && !opt?.height && !opt?.fit)
      return this.urlFor(id)
        .format(format)
        .width(opt.width)
        .fit(defaultFit)
        .url();
    // height, fit
    else if (!opt?.width && opt?.height && opt?.fit)
      return String(
        this.urlFor(id).format(format).height(opt.height).fit(opt.fit).url()
      );
    // height, (default fit)
    else if (!opt?.width && opt?.height && !opt?.fit)
      return String(
        this.urlFor(id).format(format).height(opt.height).fit(defaultFit).url()
      );

    return String(this.urlFor(id).format(format).url());
  };

  getSanityImageSizesObject = (
    id: string | null | undefined,
    opt: SanityImageSizesOpt
  ): SanityImageSizesObject => {
    const smallestSize = opt.sizes[0];
    const getSrcset = (
      id: string,
      opt: SanityImageSizesOpt,
      format?: ImageFormat
    ): string[] =>
      opt.sizes.map((size) =>
        this.getSanityImageSrc(id, {
          format: format || 'jpg',
          width: size[0],
          height: size[1],
          fit: opt.fit,
          hotspot: opt.hotspot
        })
      );

    return id
      ? {
        src: this.getSanityImageSrc(id, {
          width: smallestSize[0],
          height: smallestSize[1],
          fit: opt.fit,
          hotspot: opt.hotspot
        }),
        srcset: getSrcset(id, opt).join(', '),
        srcsetWebp: getSrcset(id, opt, 'webp').join(', ')
      }
      : {};
  };
}
